import { useState, useMemo } from 'react';
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, type SortingState } from '@tanstack/react-table';
import { X, Download } from 'lucide-react';
import type { NlqResult } from '../engine/NlqEngine';
interface Props {
  result: NlqResult;
  onClose: () => void;
}
export function ResultsOverlay({
  result,
  onClose
}: Props) {
  const rows = result.results?.rows ?? [];
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = useMemo(() => {
    if (rows.length === 0) return [];
    const allKeys = new Set<string>();
    for (const row of rows) {
      for (const key of Object.keys(row)) {
        allKeys.add(key);
      }
    }
    return Array.from(allKeys).map(key => ({
      accessorKey: key,
      header: key,
      cell: (info: {
        getValue: () => unknown;
      }) => {
        const val = info.getValue();
        if (val === null || val === undefined) return '-';
        if (typeof val === 'number') return val.toLocaleString();
        return String(val);
      }
    }));
  }, [rows]);
  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });
  const handleExport = (scope: 'selected' | 'page' | 'all') => {
    let dataToExport: Record<string, unknown>[];
    if (scope === 'page') {
      dataToExport = table.getRowModel().rows.map(r => r.original);
    } else {
      dataToExport = rows;
    }
    if (dataToExport.length === 0) return;
    const headers = Object.keys(dataToExport[0]);
    const csvRows = [headers.join(','), ...dataToExport.map(row => headers.map(h => {
      const val = row[h];
      const str = val === null || val === undefined ? '' : String(val);
      return str.includes(',') ? `"${str}"` : str;
    }).join(','))];
    const blob = new Blob([csvRows.join('\n')], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ondemand-results-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <div className="nlq-overlay" onClick={onClose} data-id="element-830" data-mp="qtmfmmckdd2g">
      <div className="nlq-overlay-content" onClick={e => e.stopPropagation()} data-id="element-831" data-mp="lu04qjk7k8wt">
        <div className="nlq-overlay-header" data-id="element-832" data-mp="1ij7eyvfdmvlf">
          <h3 data-id="element-833" data-mp="1il21ufa7kxs5">
            Results ({result.results?.metadata.totalRows ?? 0} rows)
          </h3>
          <div className="nlq-overlay-actions" data-id="element-834" data-mp="1ejgtn78md7oa">
            <button onClick={() => handleExport('page')} className="nlq-export-btn" data-id="element-835" data-mp="19pue97b2mpos">
              <Download size={14} data-id="element-836" data-mp="14rst8gu34gao" /> Export page
            </button>
            <button onClick={() => handleExport('all')} className="nlq-export-btn" data-id="element-837" data-mp="14pgfygz9uu3y">
              <Download size={14} data-id="element-838" data-mp="2289g8d3cq368" /> Export all
            </button>
            <button onClick={onClose} className="nlq-close-btn" data-id="element-839" data-mp="1xf6x6yw7ac4a">
              <X size={16} data-id="element-840" data-mp="24rcx6nxrkbc5" />
            </button>
          </div>
        </div>

        {result.explanation && <div className="nlq-explanation" data-id="element-841" data-mp="29pxvghtggkxy">{result.explanation}</div>}

        <div className="nlq-table-wrapper" data-id="element-842" data-mp="2epk9gth3y57t">
          <table className="nlq-table" data-id="element-843" data-mp="2eohhei81twqb">
            <thead data-id="element-844" data-mp="2ern6hxgogym1">
              {table.getHeaderGroups().map(headerGroup => <tr key={headerGroup.id} data-id="element-845" data-mp="2isgz3452f1l9">
                  {headerGroup.headers.map(header => <th key={header.id} onClick={header.column.getToggleSortingHandler()} className="nlq-th" data-id="element-846" data-mp="2npyzg650o8hn">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                  asc: ' ↑',
                  desc: ' ↓'
                }[header.column.getIsSorted() as string] ?? ''}
                    </th>)}
                </tr>)}
            </thead>
            <tbody data-id="element-847" data-mp="2xsazsceitlz0">
              {table.getRowModel().rows.map(row => <tr key={row.id} className="nlq-tr" data-id="element-848" data-mp="2trhfdt0aepav">
                  {row.getVisibleCells().map(cell => <td key={cell.id} className="nlq-td" data-id="element-849" data-mp="37jferc4756tu">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}